import { createContext, useContext, useEffect, useState } from 'react';
import { refreshCsrfToken, getCsrfToken, validateCsrfToken } from '@/lib/csrf';
import { useAuth } from '@/lib/auth';

interface CsrfContextType {
  csrfToken: string | null;
  refreshToken: () => string;
  validateToken: (token: string) => boolean;
}

const CsrfContext = createContext<CsrfContextType | undefined>(undefined);

export function CsrfProvider({ children }: { children: React.ReactNode }) {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const { user } = useAuth();
  
  // Generate a new token when the component mounts or user changes
  useEffect(() => {
    const token = refreshCsrfToken();
    setCsrfToken(token);
  }, [user?.id]); // Refresh when user changes
  
  const refreshToken = () => {
    const token = refreshCsrfToken();
    setCsrfToken(token);
    return token;
  };
  
  const validateToken = (token: string) => {
    return validateCsrfToken(token);
  };
  
  return (
    <CsrfContext.Provider value={{ csrfToken, refreshToken, validateToken }}>
      {children}
    </CsrfContext.Provider>
  );
}

export const useCsrf = () => {
  const context = useContext(CsrfContext);
  if (context === undefined) {
    throw new Error('useCsrf must be used within a CsrfProvider');
  }
  return context;
}; 